import type { Language, Locale } from '@/config/locales';

export interface MenuOptionTranslation {
  menuSelection: string;
  menuQuantity: string;
  required: string;
  optional: string;
  multipleSelection: string;
  unlimited: string;
  exactCount: string;
  rangeCount: string;
  quantityControl: string;
  addToCart: string;
  pleaseSelectRequired: string;
  pleaseSelectExactly: string;
  pleaseSelectMinimum: string;
  pleaseSelectRange: string;
  pleaseSelectOption: string;
  maximumSelection: string;
  /**
   * #492 (A1): 입력형 옵션 (number_input/text_input/textarea) 안내 메시지.
   * #493 fully orderable 진입 시 정식 입력 UI 로 대체 예정.
   */
  inputTypePending: string;
  /** #499: validation_rules 검증 에러 메시지 (locale 별 i18n). */
  validationMinValue: string;
  validationMaxValue: string;
  validationMinLength: string;
  validationMaxLength: string;
  validationPattern: string;
  validationNumberRequired: string;
  menu: {
    complexTestMenu: string;
    complexTestMenuDescription: string;
  };
  optionTypes: {
    required: string;
    optional: string;
    requiredSingleDescription: string;
    optionalSingleDescription: string;
    multiUnlimitedDescription: string;
    multiExactDescription: string;
    multiRangeDescription: string;
    quantityControlDescription: string;
  };
  selectionStatus: {
    totalCount: string;
    selectedCount: string;
  };
  optionGroupDescriptions: {
    selectMainIngredient: string;
    selectSize: string;
    selectToppings: string;
    selectSauces: string;
    selectSpiceLevel: string;
    selectExtras: string;
  };
  optionGroups: {
    mainChoice: string;
    sideChoice: string;
    size: string;
    spiceLevel: string;
    extras: string;
    sauce: string;
    temperature: string;
    toppings: string;
  };
  options: Record<string, string>;
}

// #512 — Locale 단일 출처 정합.
export type ModalTranslations =
  & Record<Language, MenuOptionTranslation>
  & Record<Exclude<Locale, Language>, MenuOptionTranslation>;

export const modalTranslations: ModalTranslations = {
  ko: {
    menuSelection: '메뉴 선택',
    menuQuantity: '수량',
    required: '필수',
    optional: '선택',
    multipleSelection: '복수 선택',
    unlimited: '무제한',
    exactCount: '정확히 {count}개',
    rangeCount: '{min}~{max}개',
    quantityControl: '수량 조절',
    addToCart: '장바구니 담기',
    pleaseSelectRequired: '필수 옵션을 선택해주세요',
    pleaseSelectExactly: '정확히 {count}개 선택해주세요',
    pleaseSelectMinimum: '최소 {count}개 선택해주세요',
    pleaseSelectRange: '{min}~{max}개 선택해주세요',
    pleaseSelectOption: '옵션을 선택해주세요',
    maximumSelection: '최대 {max}개까지만 선택 가능합니다',
    inputTypePending: '입력형 옵션은 곧 지원됩니다',
    validationMinValue: '최소 {min} 이상 입력해주세요',
    validationMaxValue: '최대 {max} 이하로 입력해주세요',
    validationMinLength: '최소 {min}자 이상 입력해주세요',
    validationMaxLength: '최대 {max}자 이하로 입력해주세요',
    validationPattern: '입력 형식이 올바르지 않습니다',
    validationNumberRequired: '숫자를 입력해주세요',
    menu: {
      complexTestMenu: '복합 옵션 테스트 메뉴',
      complexTestMenuDescription: '모든 종류의 옵션 조건을 지원하는 테스트 메뉴입니다'
    },
    optionTypes: {
      required: '필수',
      optional: '선택',
      requiredSingleDescription: '1개 필수 선택',
      optionalSingleDescription: '0~1개 선택',
      multiUnlimitedDescription: '무제한 선택',
      multiExactDescription: '정확히 {count}개',
      multiRangeDescription: '{min}~{max}개 선택',
      quantityControlDescription: '수량별 선택'
    },
    selectionStatus: {
      totalCount: '총 {count}개',
      selectedCount: '{count}개 선택됨'
    },
    optionGroupDescriptions: {
      selectMainIngredient: '메인 재료를 선택해주세요',
      selectSize: '사이즈를 선택하실 수 있습니다',
      selectToppings: '토핑을 원하는 만큼 선택하세요',
      selectSauces: '소스를 선택해주세요',
      selectSpiceLevel: '매운 정도를 선택하세요',
      selectExtras: '추가 옵션을 선택하세요'
    },
    optionGroups: {
      mainChoice: '메인 선택',
      sideChoice: '사이드 선택',
      size: '사이즈',
      spiceLevel: '매운맛 정도',
      extras: '추가 옵션',
      sauce: '소스',
      temperature: '온도',
      toppings: '토핑'
    },
    options: {
      beef: '소고기',
      chicken: '닭고기',
      pork: '돼지고기',
      salmon: '연어',
      friedPotato: '구운 감자',
      rice: '밥',
      salad: '샐러드',
      soup: '스프',
      small: '작은 사이즈',
      medium: '보통 사이즈',
      large: '큰 사이즈',
      cheese: '치즈',
      bacon: '베이컨',
      mushroom: '버섯',
      onion: '양파',
      lettuce: '상추',
      ketchup: '케첩',
      mustard: '머스타드',
      mayo: '마요네즈',
      bbq: 'BBQ 소스',
      hot: '핫소스',
      garlic: '갈릭소스',
      mild: '순한맛',
      medium_spice: '보통맛',
      spicy: '매운맛',
      extra_spicy: '아주 매운맛',
      super_spicy: '초매운맛',
      extra_meat: '고기 추가',
      extra_cheese: '치즈 추가',
      extra_vegetable: '야채 추가',
      extra_sauce: '소스 추가'
    }
  },
  es: {
    menuSelection: 'Selección de Menú',
    menuQuantity: 'Cantidad',
    required: 'Obligatorio',
    optional: 'Opcional',
    multipleSelection: 'Selección Múltiple',
    unlimited: 'Ilimitado',
    exactCount: 'Exactamente {count}',
    rangeCount: '{min}~{max}',
    quantityControl: 'Control de Cantidad',
    addToCart: 'Agregar al Carrito',
    pleaseSelectRequired: 'Por favor selecciona las opciones obligatorias',
    pleaseSelectExactly: 'Por favor selecciona exactamente {count} opciones',
    pleaseSelectMinimum: 'Por favor selecciona mínimo {count} opciones',
    pleaseSelectRange: 'Por favor selecciona {min}~{max} opciones',
    pleaseSelectOption: 'Por favor selecciona una opción',
    maximumSelection: 'Puedes seleccionar máximo {max} opciones',
    inputTypePending: 'Las opciones de entrada estarán disponibles pronto',
    validationMinValue: 'Ingresa al menos {min}',
    validationMaxValue: 'Ingresa máximo {max}',
    validationMinLength: 'Ingresa al menos {min} caracteres',
    validationMaxLength: 'Ingresa máximo {max} caracteres',
    validationPattern: 'El formato no es válido',
    validationNumberRequired: 'Ingresa un número',
    menu: {
      complexTestMenu: 'Menú de Prueba con Opciones Complejas',
      complexTestMenuDescription: 'Un menú de prueba que admite todo tipo de condiciones de opciones'
    },
    optionTypes: {
      required: 'Obligatorio',
      optional: 'Opcional',
      requiredSingleDescription: 'Selecciona 1 obligatorio',
      optionalSingleDescription: 'Selecciona 0~1',
      multiUnlimitedDescription: 'Selección ilimitada',
      multiExactDescription: 'Exactamente {count}',
      multiRangeDescription: 'Selecciona {min}~{max}',
      quantityControlDescription: 'Selección por cantidad'
    },
    selectionStatus: {
      totalCount: 'Total {count}',
      selectedCount: '{count} seleccionado(s)'
    },
    optionGroupDescriptions: {
      selectMainIngredient: 'Por favor selecciona el ingrediente principal',
      selectSize: 'Puedes seleccionar el tamaño',
      selectToppings: 'Selecciona todos los ingredientes que desees',
      selectSauces: 'Por favor selecciona una salsa',
      selectSpiceLevel: 'Selecciona el nivel de picante',
      selectExtras: 'Selecciona opciones adicionales'
    },
    optionGroups: {
      mainChoice: 'Elección Principal',
      sideChoice: 'Elección de Acompañante',
      size: 'Tamaño',
      spiceLevel: 'Nivel de Picante',
      extras: 'Extras',
      sauce: 'Salsa',
      temperature: 'Temperatura',
      toppings: 'Ingredientes'
    },
    options: {
      beef: 'Carne de Res',
      chicken: 'Pollo',
      pork: 'Cerdo',
      salmon: 'Salmón',
      friedPotato: 'Papa Horneada',
      rice: 'Arroz',
      salad: 'Ensalada',
      soup: 'Sopa',
      small: 'Tamaño Pequeño',
      medium: 'Tamaño Mediano',
      large: 'Tamaño Grande',
      cheese: 'Queso',
      bacon: 'Tocino',
      mushroom: 'Champiñones',
      onion: 'Cebolla',
      lettuce: 'Lechuga',
      ketchup: 'Ketchup',
      mustard: 'Mostaza',
      mayo: 'Mayonesa',
      bbq: 'Salsa BBQ',
      hot: 'Salsa Picante',
      garlic: 'Salsa de Ajo',
      mild: 'Suave',
      medium_spice: 'Medio',
      spicy: 'Picante',
      extra_spicy: 'Muy Picante',
      super_spicy: 'Super Picante',
      extra_meat: 'Carne Extra',
      extra_cheese: 'Queso Extra',
      extra_vegetable: 'Vegetales Extra',
      extra_sauce: 'Salsa Extra'
    }
  },
  en: {
    menuSelection: 'Menu Selection',
    menuQuantity: 'Quantity',
    required: 'Required',
    optional: 'Optional',
    multipleSelection: 'Multiple Selection',
    unlimited: 'Unlimited',
    exactCount: 'Exactly {count}',
    rangeCount: '{min}~{max}',
    quantityControl: 'Quantity Control',
    addToCart: 'Add to Cart',
    pleaseSelectRequired: 'Please select required options',
    pleaseSelectExactly: 'Please select exactly {count} options',
    pleaseSelectMinimum: 'Please select minimum {count} options',
    pleaseSelectRange: 'Please select {min}~{max} options',
    pleaseSelectOption: 'Please select an option',
    maximumSelection: 'You can select maximum {max} options',
    inputTypePending: 'Input options will be supported soon',
    validationMinValue: 'Enter at least {min}',
    validationMaxValue: 'Enter at most {max}',
    validationMinLength: 'Enter at least {min} characters',
    validationMaxLength: 'Enter at most {max} characters',
    validationPattern: 'Invalid format',
    validationNumberRequired: 'Enter a number',
    menu: {
      complexTestMenu: 'Complex Option Test Menu',
      complexTestMenuDescription: 'A test menu that supports all kinds of option conditions'
    },
    optionTypes: {
      required: 'Required',
      optional: 'Optional',
      requiredSingleDescription: 'Select 1 required',
      optionalSingleDescription: 'Select 0~1',
      multiUnlimitedDescription: 'Unlimited selection',
      multiExactDescription: 'Exactly {count}',
      multiRangeDescription: 'Select {min}~{max}',
      quantityControlDescription: 'Select by quantity'
    },
    selectionStatus: {
      totalCount: 'Total {count}',
      selectedCount: '{count} selected'
    },
    optionGroupDescriptions: {
      selectMainIngredient: 'Please select the main ingredient',
      selectSize: 'You can select a size',
      selectToppings: 'Select as many toppings as you like',
      selectSauces: 'Please select a sauce',
      selectSpiceLevel: 'Select spice level',
      selectExtras: 'Select additional options'
    },
    optionGroups: {
      mainChoice: 'Main Choice',
      sideChoice: 'Side Choice',
      size: 'Size',
      spiceLevel: 'Spice Level',
      extras: 'Extras',
      sauce: 'Sauce',
      temperature: 'Temperature',
      toppings: 'Toppings'
    },
    options: {
      beef: 'Beef',
      chicken: 'Chicken',
      pork: 'Pork',
      salmon: 'Salmon',
      friedPotato: 'Fried Potato',
      rice: 'Rice',
      salad: 'Salad',
      soup: 'Soup',
      small: 'Small Size',
      medium: 'Medium Size',
      large: 'Large Size',
      cheese: 'Cheese',
      bacon: 'Bacon',
      mushroom: 'Mushroom',
      onion: 'Onion',
      lettuce: 'Lettuce',
      ketchup: 'Ketchup',
      mustard: 'Mustard',
      mayo: 'Mayonnaise',
      bbq: 'BBQ Sauce',
      hot: 'Hot Sauce',
      garlic: 'Garlic Sauce',
      mild: 'Mild',
      medium_spice: 'Medium',
      spicy: 'Spicy',
      extra_spicy: 'Extra Spicy',
      super_spicy: 'Super Spicy',
      extra_meat: 'Extra Meat',
      extra_cheese: 'Extra Cheese',
      extra_vegetable: 'Extra Vegetables',
      extra_sauce: 'Extra Sauce'
    }
  },
  pt: {
    menuSelection: 'Seleção de menu', menuQuantity: 'Quantidade', required: 'Obrigatório', optional: 'Opcional',
    multipleSelection: 'Seleção múltipla', unlimited: 'Ilimitado', exactCount: 'Exatamente {count}', rangeCount: '{min}~{max}',
    quantityControl: 'Controle de quantidade', addToCart: 'Adicionar ao carrinho',
    pleaseSelectRequired: 'Por favor, selecione as opções obrigatórias', pleaseSelectExactly: 'Por favor, selecione exatamente {count} opções',
    pleaseSelectMinimum: 'Por favor, selecione mínimo {count} opções', pleaseSelectRange: 'Por favor, selecione {min}~{max} opções',
    pleaseSelectOption: 'Por favor, selecione uma opção', maximumSelection: 'Pode selecionar máximo {max} opções',
    inputTypePending: 'Opções de entrada serão suportadas em breve',
    validationMinValue: 'Insira pelo menos {min}', validationMaxValue: 'Insira no máximo {max}',
    validationMinLength: 'Insira pelo menos {min} caracteres', validationMaxLength: 'Insira no máximo {max} caracteres',
    validationPattern: 'Formato inválido', validationNumberRequired: 'Insira um número',
    menu: { complexTestMenu: 'Menu de teste com opções complexas', complexTestMenuDescription: 'Um menu de teste que suporta todos os tipos de condições de opções' },
    optionTypes: { required: 'Obrigatório', optional: 'Opcional', requiredSingleDescription: 'Selecione 1 obrigatório', optionalSingleDescription: 'Selecione 0~1', multiUnlimitedDescription: 'Seleção ilimitada', multiExactDescription: 'Exatamente {count}', multiRangeDescription: 'Selecione {min}~{max}', quantityControlDescription: 'Seleção por quantidade' },
    selectionStatus: { totalCount: 'Total {count}', selectedCount: '{count} selecionado(s)' },
    optionGroupDescriptions: { selectMainIngredient: 'Por favor, selecione o ingrediente principal', selectSize: 'Pode selecionar o tamanho', selectToppings: 'Selecione quantos toppings desejar', selectSauces: 'Por favor, selecione um molho', selectSpiceLevel: 'Selecione o nível de picância', selectExtras: 'Selecione opções adicionais' },
    optionGroups: { mainChoice: 'Escolha principal', sideChoice: 'Escolha lateral', size: 'Tamanho', spiceLevel: 'Nível de picância', extras: 'Extras', sauce: 'Molho', temperature: 'Temperatura', toppings: 'Toppings' },
    options: { beef: 'Carne bovina', chicken: 'Frango', pork: 'Carne suína', salmon: 'Salmão', friedPotato: 'Batata assada', rice: 'Arroz', salad: 'Salada', soup: 'Sopa', small: 'Tamanho pequeno', medium: 'Tamanho médio', large: 'Tamanho grande', cheese: 'Queijo', bacon: 'Bacon', mushroom: 'Cogumelos', onion: 'Cebola', lettuce: 'Alface', ketchup: 'Ketchup', mustard: 'Mostarda', mayo: 'Maionese', bbq: 'Molho BBQ', hot: 'Molho picante', garlic: 'Molho de alho', mild: 'Suave', medium_spice: 'Médio', spicy: 'Picante', extra_spicy: 'Muito picante', super_spicy: 'Super picante', extra_meat: 'Carne extra', extra_cheese: 'Queijo extra', extra_vegetable: 'Vegetais extras', extra_sauce: 'Molho extra' },
  },
  fr: {
    menuSelection: 'Sélection du menu', menuQuantity: 'Quantité', required: 'Obligatoire', optional: 'Facultatif',
    multipleSelection: 'Sélection multiple', unlimited: 'Illimité', exactCount: 'Exactement {count}', rangeCount: '{min}~{max}',
    quantityControl: 'Contrôle de quantité', addToCart: 'Ajouter au panier',
    pleaseSelectRequired: 'Veuillez sélectionner les options obligatoires', pleaseSelectExactly: 'Veuillez sélectionner exactement {count} options',
    pleaseSelectMinimum: 'Veuillez sélectionner au moins {count} options', pleaseSelectRange: 'Veuillez sélectionner {min}~{max} options',
    pleaseSelectOption: 'Veuillez sélectionner une option', maximumSelection: 'Vous pouvez sélectionner maximum {max} options',
    inputTypePending: 'Les options de saisie seront bientôt disponibles',
    validationMinValue: 'Saisissez au moins {min}', validationMaxValue: 'Saisissez au maximum {max}',
    validationMinLength: 'Saisissez au moins {min} caractères', validationMaxLength: 'Saisissez au maximum {max} caractères',
    validationPattern: 'Format invalide', validationNumberRequired: 'Saisissez un nombre',
    menu: { complexTestMenu: 'Menu de test avec options complexes', complexTestMenuDescription: 'Un menu de test qui prend en charge tous les types de conditions d’options' },
    optionTypes: { required: 'Obligatoire', optional: 'Facultatif', requiredSingleDescription: 'Sélectionnez 1 obligatoire', optionalSingleDescription: 'Sélectionnez 0~1', multiUnlimitedDescription: 'Sélection illimitée', multiExactDescription: 'Exactement {count}', multiRangeDescription: 'Sélectionnez {min}~{max}', quantityControlDescription: 'Sélection par quantité' },
    selectionStatus: { totalCount: 'Total {count}', selectedCount: '{count} sélectionné(s)' },
    optionGroupDescriptions: { selectMainIngredient: 'Veuillez sélectionner l’ingrédient principal', selectSize: 'Vous pouvez sélectionner une taille', selectToppings: 'Sélectionnez autant de garnitures que vous le souhaitez', selectSauces: 'Veuillez sélectionner une sauce', selectSpiceLevel: 'Sélectionnez le niveau de piquant', selectExtras: 'Sélectionnez des options supplémentaires' },
    optionGroups: { mainChoice: 'Choix principal', sideChoice: 'Choix d’accompagnement', size: 'Taille', spiceLevel: 'Niveau de piquant', extras: 'Suppléments', sauce: 'Sauce', temperature: 'Température', toppings: 'Garnitures' },
    options: { beef: 'Bœuf', chicken: 'Poulet', pork: 'Porc', salmon: 'Saumon', friedPotato: 'Pomme de terre frite', rice: 'Riz', salad: 'Salade', soup: 'Soupe', small: 'Petit', medium: 'Moyen', large: 'Grand', cheese: 'Fromage', bacon: 'Bacon', mushroom: 'Champignons', onion: 'Oignon', lettuce: 'Laitue', ketchup: 'Ketchup', mustard: 'Moutarde', mayo: 'Mayonnaise', bbq: 'Sauce BBQ', hot: 'Sauce piquante', garlic: 'Sauce à l’ail', mild: 'Doux', medium_spice: 'Moyen', spicy: 'Piquant', extra_spicy: 'Très piquant', super_spicy: 'Super piquant', extra_meat: 'Viande extra', extra_cheese: 'Fromage extra', extra_vegetable: 'Légumes extra', extra_sauce: 'Sauce extra' },
  },
  zh: {
    menuSelection: '菜单选择', menuQuantity: '数量', required: '必选', optional: '可选',
    multipleSelection: '多选', unlimited: '无限制', exactCount: '精确 {count} 个', rangeCount: '{min}~{max} 个',
    quantityControl: '数量控制', addToCart: '加入购物车',
    pleaseSelectRequired: '请选择必选项', pleaseSelectExactly: '请精确选择 {count} 个选项',
    pleaseSelectMinimum: '请至少选择 {count} 个选项', pleaseSelectRange: '请选择 {min}~{max} 个选项',
    pleaseSelectOption: '请选择一个选项', maximumSelection: '最多可以选择 {max} 个选项',
    inputTypePending: '输入选项即将支持',
    validationMinValue: '请输入至少 {min}', validationMaxValue: '请输入最多 {max}',
    validationMinLength: '请输入至少 {min} 个字符', validationMaxLength: '请输入最多 {max} 个字符',
    validationPattern: '格式无效', validationNumberRequired: '请输入数字',
    menu: { complexTestMenu: '复杂选项测试菜单', complexTestMenuDescription: '支持所有类型选项条件的测试菜单' },
    optionTypes: { required: '必选', optional: '可选', requiredSingleDescription: '必选 1 个', optionalSingleDescription: '可选 0~1 个', multiUnlimitedDescription: '无限选择', multiExactDescription: '精确 {count} 个', multiRangeDescription: '选择 {min}~{max} 个', quantityControlDescription: '按数量选择' },
    selectionStatus: { totalCount: '共 {count} 个', selectedCount: '已选 {count} 个' },
    optionGroupDescriptions: { selectMainIngredient: '请选择主要食材', selectSize: '您可以选择尺寸', selectToppings: '想要多少配料就选多少', selectSauces: '请选择酱料', selectSpiceLevel: '选择辣度', selectExtras: '选择额外选项' },
    optionGroups: { mainChoice: '主选', sideChoice: '配菜选择', size: '尺寸', spiceLevel: '辣度', extras: '附加', sauce: '酱料', temperature: '温度', toppings: '配料' },
    options: { beef: '牛肉', chicken: '鸡肉', pork: '猪肉', salmon: '三文鱼', friedPotato: '烤土豆', rice: '米饭', salad: '沙拉', soup: '汤', small: '小份', medium: '中份', large: '大份', cheese: '奶酪', bacon: '培根', mushroom: '蘑菇', onion: '洋葱', lettuce: '生菜', ketchup: '番茄酱', mustard: '芥末酱', mayo: '蛋黄酱', bbq: 'BBQ 酱', hot: '辣酱', garlic: '蒜蓉酱', mild: '微辣', medium_spice: '中辣', spicy: '辣', extra_spicy: '特辣', super_spicy: '超辣', extra_meat: '加肉', extra_cheese: '加奶酪', extra_vegetable: '加蔬菜', extra_sauce: '加酱料' },
  },
  ja: {
    menuSelection: 'メニュー選択', menuQuantity: '数量', required: '必須', optional: 'オプション',
    multipleSelection: '複数選択', unlimited: '無制限', exactCount: '正確に {count} 個', rangeCount: '{min}~{max} 個',
    quantityControl: '数量調節', addToCart: 'カートに入れる',
    pleaseSelectRequired: '必須オプションを選択してください', pleaseSelectExactly: '正確に {count} 個選択してください',
    pleaseSelectMinimum: '最低 {count} 個選択してください', pleaseSelectRange: '{min}~{max} 個選択してください',
    pleaseSelectOption: 'オプションを選択してください', maximumSelection: '最大 {max} 個まで選択可能です',
    inputTypePending: '入力オプションは近日対応予定です',
    validationMinValue: '最低 {min} 以上を入力してください', validationMaxValue: '最大 {max} 以下を入力してください',
    validationMinLength: '最低 {min} 文字以上入力してください', validationMaxLength: '最大 {max} 文字以下入力してください',
    validationPattern: '形式が正しくありません', validationNumberRequired: '数字を入力してください',
    menu: { complexTestMenu: '複合オプションテストメニュー', complexTestMenuDescription: 'あらゆる種類のオプション条件をサポートするテストメニュー' },
    optionTypes: { required: '必須', optional: 'オプション', requiredSingleDescription: '1 個必須選択', optionalSingleDescription: '0~1 個選択', multiUnlimitedDescription: '無制限選択', multiExactDescription: '正確に {count} 個', multiRangeDescription: '{min}~{max} 個選択', quantityControlDescription: '数量別選択' },
    selectionStatus: { totalCount: '合計 {count} 個', selectedCount: '{count} 個選択済み' },
    optionGroupDescriptions: { selectMainIngredient: 'メイン食材を選択してください', selectSize: 'サイズを選択できます', selectToppings: 'お好みのトッピングを選んでください', selectSauces: 'ソースを選択してください', selectSpiceLevel: '辛さレベルを選択してください', selectExtras: '追加オプションを選択してください' },
    optionGroups: { mainChoice: 'メイン選択', sideChoice: 'サイド選択', size: 'サイズ', spiceLevel: '辛さレベル', extras: '追加', sauce: 'ソース', temperature: '温度', toppings: 'トッピング' },
    options: { beef: '牛肉', chicken: '鶏肉', pork: '豚肉', salmon: 'サーモン', friedPotato: '焼きじゃがいも', rice: 'ご飯', salad: 'サラダ', soup: 'スープ', small: '小サイズ', medium: '中サイズ', large: '大サイズ', cheese: 'チーズ', bacon: 'ベーコン', mushroom: 'マッシュルーム', onion: '玉ねぎ', lettuce: 'レタス', ketchup: 'ケチャップ', mustard: 'マスタード', mayo: 'マヨネーズ', bbq: 'BBQ ソース', hot: 'ホットソース', garlic: 'ガーリックソース', mild: 'マイルド', medium_spice: '普通', spicy: '辛口', extra_spicy: '激辛', super_spicy: '超激辛', extra_meat: '肉追加', extra_cheese: 'チーズ追加', extra_vegetable: '野菜追加', extra_sauce: 'ソース追加' },
  },
  hi: {
    menuSelection: 'मेनू चयन', menuQuantity: 'मात्रा', required: 'आवश्यक', optional: 'वैकल्पिक',
    multipleSelection: 'एकाधिक चयन', unlimited: 'असीमित', exactCount: 'ठीक {count}', rangeCount: '{min}~{max}',
    quantityControl: 'मात्रा नियंत्रण', addToCart: 'कार्ट में जोड़ें',
    pleaseSelectRequired: 'कृपया आवश्यक विकल्प चुनें', pleaseSelectExactly: 'कृपया ठीक {count} विकल्प चुनें',
    pleaseSelectMinimum: 'कृपया कम से कम {count} विकल्प चुनें', pleaseSelectRange: 'कृपया {min}~{max} विकल्प चुनें',
    pleaseSelectOption: 'कृपया एक विकल्प चुनें', maximumSelection: 'आप अधिकतम {max} विकल्प चुन सकते हैं',
    inputTypePending: 'इनपुट विकल्प जल्द ही समर्थित होंगे',
    validationMinValue: 'कम से कम {min} दर्ज करें', validationMaxValue: 'अधिकतम {max} दर्ज करें',
    validationMinLength: 'कम से कम {min} वर्ण दर्ज करें', validationMaxLength: 'अधिकतम {max} वर्ण दर्ज करें',
    validationPattern: 'अमान्य प्रारूप', validationNumberRequired: 'एक संख्या दर्ज करें',
    menu: { complexTestMenu: 'जटिल विकल्प परीक्षण मेनू', complexTestMenuDescription: 'सभी प्रकार के विकल्प स्थितियों का समर्थन करने वाला परीक्षण मेनू' },
    optionTypes: { required: 'आवश्यक', optional: 'वैकल्पिक', requiredSingleDescription: '1 आवश्यक चुनें', optionalSingleDescription: '0~1 चुनें', multiUnlimitedDescription: 'असीमित चयन', multiExactDescription: 'ठीक {count}', multiRangeDescription: '{min}~{max} चुनें', quantityControlDescription: 'मात्रा द्वारा चयन' },
    selectionStatus: { totalCount: 'कुल {count}', selectedCount: '{count} चयनित' },
    optionGroupDescriptions: { selectMainIngredient: 'कृपया मुख्य सामग्री चुनें', selectSize: 'आप एक आकार चुन सकते हैं', selectToppings: 'जितने टॉपिंग चाहें चुनें', selectSauces: 'कृपया एक सॉस चुनें', selectSpiceLevel: 'मसाले का स्तर चुनें', selectExtras: 'अतिरिक्त विकल्प चुनें' },
    optionGroups: { mainChoice: 'मुख्य चुनाव', sideChoice: 'साइड चुनाव', size: 'आकार', spiceLevel: 'मसाला स्तर', extras: 'अतिरिक्त', sauce: 'सॉस', temperature: 'तापमान', toppings: 'टॉपिंग्स' },
    options: { beef: 'गोमांस', chicken: 'चिकन', pork: 'सूअर का मांस', salmon: 'सैल्मन', friedPotato: 'भुना आलू', rice: 'चावल', salad: 'सलाद', soup: 'सूप', small: 'छोटा', medium: 'मध्यम', large: 'बड़ा', cheese: 'चीज़', bacon: 'बेकन', mushroom: 'मशरूम', onion: 'प्याज', lettuce: 'सलाद पत्ता', ketchup: 'केचप', mustard: 'राई', mayo: 'मेयोनीज़', bbq: 'बीबीक्यू सॉस', hot: 'गरम सॉस', garlic: 'लहसुन सॉस', mild: 'हल्का', medium_spice: 'मध्यम', spicy: 'तीखा', extra_spicy: 'अत्यधिक तीखा', super_spicy: 'सुपर तीखा', extra_meat: 'अतिरिक्त मांस', extra_cheese: 'अतिरिक्त चीज़', extra_vegetable: 'अतिरिक्त सब्जियां', extra_sauce: 'अतिरिक्त सॉस' },
  },
  de: {
    menuSelection: 'Menüauswahl', menuQuantity: 'Menge', required: 'Erforderlich', optional: 'Optional',
    multipleSelection: 'Mehrfachauswahl', unlimited: 'Unbegrenzt', exactCount: 'Genau {count}', rangeCount: '{min}~{max}',
    quantityControl: 'Mengenkontrolle', addToCart: 'In den Warenkorb',
    pleaseSelectRequired: 'Bitte wählen Sie die erforderlichen Optionen aus', pleaseSelectExactly: 'Bitte wählen Sie genau {count} Optionen aus',
    pleaseSelectMinimum: 'Bitte wählen Sie mindestens {count} Optionen aus', pleaseSelectRange: 'Bitte wählen Sie {min}~{max} Optionen aus',
    pleaseSelectOption: 'Bitte wählen Sie eine Option aus', maximumSelection: 'Sie können maximal {max} Optionen auswählen',
    inputTypePending: 'Eingabeoptionen werden bald unterstützt',
    validationMinValue: 'Geben Sie mindestens {min} ein', validationMaxValue: 'Geben Sie maximal {max} ein',
    validationMinLength: 'Geben Sie mindestens {min} Zeichen ein', validationMaxLength: 'Geben Sie maximal {max} Zeichen ein',
    validationPattern: 'Ungültiges Format', validationNumberRequired: 'Geben Sie eine Zahl ein',
    menu: { complexTestMenu: 'Testmenü mit komplexen Optionen', complexTestMenuDescription: 'Ein Testmenü, das alle Arten von Optionsbedingungen unterstützt' },
    optionTypes: { required: 'Erforderlich', optional: 'Optional', requiredSingleDescription: '1 erforderlich auswählen', optionalSingleDescription: '0~1 auswählen', multiUnlimitedDescription: 'Unbegrenzte Auswahl', multiExactDescription: 'Genau {count}', multiRangeDescription: '{min}~{max} auswählen', quantityControlDescription: 'Auswahl nach Menge' },
    selectionStatus: { totalCount: 'Insgesamt {count}', selectedCount: '{count} ausgewählt' },
    optionGroupDescriptions: { selectMainIngredient: 'Bitte wählen Sie die Hauptzutat aus', selectSize: 'Sie können eine Größe auswählen', selectToppings: 'Wählen Sie so viele Toppings wie Sie möchten', selectSauces: 'Bitte wählen Sie eine Sauce aus', selectSpiceLevel: 'Wählen Sie den Schärfegrad', selectExtras: 'Wählen Sie zusätzliche Optionen' },
    optionGroups: { mainChoice: 'Hauptauswahl', sideChoice: 'Beilagenauswahl', size: 'Größe', spiceLevel: 'Schärfegrad', extras: 'Extras', sauce: 'Sauce', temperature: 'Temperatur', toppings: 'Toppings' },
    options: { beef: 'Rindfleisch', chicken: 'Hähnchen', pork: 'Schweinefleisch', salmon: 'Lachs', friedPotato: 'Bratkartoffeln', rice: 'Reis', salad: 'Salat', soup: 'Suppe', small: 'Kleine Größe', medium: 'Mittlere Größe', large: 'Große Größe', cheese: 'Käse', bacon: 'Speck', mushroom: 'Pilze', onion: 'Zwiebel', lettuce: 'Salat', ketchup: 'Ketchup', mustard: 'Senf', mayo: 'Mayonnaise', bbq: 'BBQ-Sauce', hot: 'Scharfe Sauce', garlic: 'Knoblauchsauce', mild: 'Mild', medium_spice: 'Mittel', spicy: 'Scharf', extra_spicy: 'Sehr scharf', super_spicy: 'Super scharf', extra_meat: 'Extra Fleisch', extra_cheese: 'Extra Käse', extra_vegetable: 'Extra Gemüse', extra_sauce: 'Extra Sauce' },
  },
  it: {
    menuSelection: 'Selezione menu', menuQuantity: 'Quantità', required: 'Obbligatorio', optional: 'Facoltativo',
    multipleSelection: 'Selezione multipla', unlimited: 'Illimitato', exactCount: 'Esattamente {count}', rangeCount: '{min}~{max}',
    quantityControl: 'Controllo quantità', addToCart: 'Aggiungi al carrello',
    pleaseSelectRequired: 'Per favore seleziona le opzioni obbligatorie', pleaseSelectExactly: 'Per favore seleziona esattamente {count} opzioni',
    pleaseSelectMinimum: 'Per favore seleziona almeno {count} opzioni', pleaseSelectRange: 'Per favore seleziona {min}~{max} opzioni',
    pleaseSelectOption: 'Per favore seleziona un’opzione', maximumSelection: 'Puoi selezionare massimo {max} opzioni',
    inputTypePending: 'Le opzioni di input saranno presto supportate',
    validationMinValue: 'Inserisci almeno {min}', validationMaxValue: 'Inserisci massimo {max}',
    validationMinLength: 'Inserisci almeno {min} caratteri', validationMaxLength: 'Inserisci massimo {max} caratteri',
    validationPattern: 'Formato non valido', validationNumberRequired: 'Inserisci un numero',
    menu: { complexTestMenu: 'Menu di test con opzioni complesse', complexTestMenuDescription: 'Un menu di test che supporta tutti i tipi di condizioni di opzioni' },
    optionTypes: { required: 'Obbligatorio', optional: 'Facoltativo', requiredSingleDescription: 'Seleziona 1 obbligatorio', optionalSingleDescription: 'Seleziona 0~1', multiUnlimitedDescription: 'Selezione illimitata', multiExactDescription: 'Esattamente {count}', multiRangeDescription: 'Seleziona {min}~{max}', quantityControlDescription: 'Selezione per quantità' },
    selectionStatus: { totalCount: 'Totale {count}', selectedCount: '{count} selezionato/i' },
    optionGroupDescriptions: { selectMainIngredient: 'Per favore seleziona l’ingrediente principale', selectSize: 'Puoi selezionare una taglia', selectToppings: 'Seleziona quanti topping desideri', selectSauces: 'Per favore seleziona una salsa', selectSpiceLevel: 'Seleziona il livello di piccantezza', selectExtras: 'Seleziona opzioni aggiuntive' },
    optionGroups: { mainChoice: 'Scelta principale', sideChoice: 'Scelta laterale', size: 'Taglia', spiceLevel: 'Livello di piccantezza', extras: 'Extra', sauce: 'Salsa', temperature: 'Temperatura', toppings: 'Topping' },
    options: { beef: 'Manzo', chicken: 'Pollo', pork: 'Maiale', salmon: 'Salmone', friedPotato: 'Patate al forno', rice: 'Riso', salad: 'Insalata', soup: 'Zuppa', small: 'Piccolo', medium: 'Medio', large: 'Grande', cheese: 'Formaggio', bacon: 'Pancetta', mushroom: 'Funghi', onion: 'Cipolla', lettuce: 'Lattuga', ketchup: 'Ketchup', mustard: 'Senape', mayo: 'Maionese', bbq: 'Salsa BBQ', hot: 'Salsa piccante', garlic: 'Salsa all’aglio', mild: 'Delicato', medium_spice: 'Medio', spicy: 'Piccante', extra_spicy: 'Molto piccante', super_spicy: 'Super piccante', extra_meat: 'Carne extra', extra_cheese: 'Formaggio extra', extra_vegetable: 'Verdure extra', extra_sauce: 'Salsa extra' },
  },
  ru: {
    menuSelection: 'Выбор меню', menuQuantity: 'Количество', required: 'Обязательно', optional: 'Необязательно',
    multipleSelection: 'Множественный выбор', unlimited: 'Без ограничений', exactCount: 'Ровно {count}', rangeCount: '{min}~{max}',
    quantityControl: 'Управление количеством', addToCart: 'В корзину',
    pleaseSelectRequired: 'Пожалуйста, выберите обязательные варианты', pleaseSelectExactly: 'Пожалуйста, выберите ровно {count} вариантов',
    pleaseSelectMinimum: 'Пожалуйста, выберите минимум {count} вариантов', pleaseSelectRange: 'Пожалуйста, выберите {min}~{max} вариантов',
    pleaseSelectOption: 'Пожалуйста, выберите вариант', maximumSelection: 'Вы можете выбрать максимум {max} вариантов',
    inputTypePending: 'Варианты ввода скоро будут поддерживаться',
    validationMinValue: 'Введите не менее {min}', validationMaxValue: 'Введите не более {max}',
    validationMinLength: 'Введите не менее {min} символов', validationMaxLength: 'Введите не более {max} символов',
    validationPattern: 'Недопустимый формат', validationNumberRequired: 'Введите число',
    menu: { complexTestMenu: 'Тестовое меню со сложными вариантами', complexTestMenuDescription: 'Тестовое меню, поддерживающее все виды условий вариантов' },
    optionTypes: { required: 'Обязательно', optional: 'Необязательно', requiredSingleDescription: 'Выберите 1 обязательный', optionalSingleDescription: 'Выберите 0~1', multiUnlimitedDescription: 'Безлимитный выбор', multiExactDescription: 'Ровно {count}', multiRangeDescription: 'Выберите {min}~{max}', quantityControlDescription: 'Выбор по количеству' },
    selectionStatus: { totalCount: 'Всего {count}', selectedCount: 'Выбрано {count}' },
    optionGroupDescriptions: { selectMainIngredient: 'Пожалуйста, выберите основной ингредиент', selectSize: 'Вы можете выбрать размер', selectToppings: 'Выберите столько начинки, сколько хотите', selectSauces: 'Пожалуйста, выберите соус', selectSpiceLevel: 'Выберите уровень остроты', selectExtras: 'Выберите дополнительные варианты' },
    optionGroups: { mainChoice: 'Основной выбор', sideChoice: 'Выбор гарнира', size: 'Размер', spiceLevel: 'Уровень остроты', extras: 'Дополнения', sauce: 'Соус', temperature: 'Температура', toppings: 'Начинка' },
    options: { beef: 'Говядина', chicken: 'Курица', pork: 'Свинина', salmon: 'Лосось', friedPotato: 'Запечённый картофель', rice: 'Рис', salad: 'Салат', soup: 'Суп', small: 'Маленький', medium: 'Средний', large: 'Большой', cheese: 'Сыр', bacon: 'Бекон', mushroom: 'Грибы', onion: 'Лук', lettuce: 'Латук', ketchup: 'Кетчуп', mustard: 'Горчица', mayo: 'Майонез', bbq: 'Соус BBQ', hot: 'Острый соус', garlic: 'Чесночный соус', mild: 'Мягкий', medium_spice: 'Средний', spicy: 'Острый', extra_spicy: 'Очень острый', super_spicy: 'Супер острый', extra_meat: 'Дополнительное мясо', extra_cheese: 'Дополнительный сыр', extra_vegetable: 'Дополнительные овощи', extra_sauce: 'Дополнительный соус' },
  },
  ar: {
    menuSelection: 'اختيار القائمة', menuQuantity: 'الكمية', required: 'مطلوب', optional: 'اختياري',
    multipleSelection: 'تحديد متعدد', unlimited: 'غير محدود', exactCount: 'بالضبط {count}', rangeCount: '{min}~{max}',
    quantityControl: 'التحكم بالكمية', addToCart: 'أضف إلى السلة',
    pleaseSelectRequired: 'يرجى اختيار الخيارات المطلوبة', pleaseSelectExactly: 'يرجى اختيار {count} خيارات بالضبط',
    pleaseSelectMinimum: 'يرجى اختيار {count} خيارات على الأقل', pleaseSelectRange: 'يرجى اختيار {min}~{max} خيارات',
    pleaseSelectOption: 'يرجى اختيار خيار', maximumSelection: 'يمكنك اختيار {max} خيارات كحد أقصى',
    inputTypePending: 'خيارات الإدخال ستكون متاحة قريباً',
    validationMinValue: 'أدخل {min} على الأقل', validationMaxValue: 'أدخل {max} كحد أقصى',
    validationMinLength: 'أدخل {min} حرف على الأقل', validationMaxLength: 'أدخل {max} حرف كحد أقصى',
    validationPattern: 'تنسيق غير صالح', validationNumberRequired: 'أدخل رقماً',
    menu: { complexTestMenu: 'قائمة اختبار بخيارات معقدة', complexTestMenuDescription: 'قائمة اختبار تدعم جميع أنواع شروط الخيارات' },
    optionTypes: { required: 'مطلوب', optional: 'اختياري', requiredSingleDescription: 'اختر 1 مطلوب', optionalSingleDescription: 'اختر 0~1', multiUnlimitedDescription: 'اختيار غير محدود', multiExactDescription: 'بالضبط {count}', multiRangeDescription: 'اختر {min}~{max}', quantityControlDescription: 'الاختيار حسب الكمية' },
    selectionStatus: { totalCount: 'الإجمالي {count}', selectedCount: 'تم اختيار {count}' },
    optionGroupDescriptions: { selectMainIngredient: 'يرجى اختيار المكون الرئيسي', selectSize: 'يمكنك اختيار حجم', selectToppings: 'اختر ما تريد من الإضافات', selectSauces: 'يرجى اختيار صلصة', selectSpiceLevel: 'اختر مستوى الحرارة', selectExtras: 'اختر خيارات إضافية' },
    optionGroups: { mainChoice: 'الاختيار الرئيسي', sideChoice: 'اختيار الأطباق الجانبية', size: 'الحجم', spiceLevel: 'مستوى الحرارة', extras: 'إضافات', sauce: 'صلصة', temperature: 'درجة الحرارة', toppings: 'إضافات' },
    options: { beef: 'لحم بقر', chicken: 'دجاج', pork: 'لحم خنزير', salmon: 'سلمون', friedPotato: 'بطاطا مشوية', rice: 'أرز', salad: 'سلطة', soup: 'حساء', small: 'صغير', medium: 'متوسط', large: 'كبير', cheese: 'جبنة', bacon: 'لحم مقدد', mushroom: 'فطر', onion: 'بصل', lettuce: 'خس', ketchup: 'كاتشب', mustard: 'خردل', mayo: 'مايونيز', bbq: 'صلصة باربكيو', hot: 'صلصة حارة', garlic: 'صلصة الثوم', mild: 'خفيف', medium_spice: 'متوسط', spicy: 'حار', extra_spicy: 'حار جداً', super_spicy: 'حار للغاية', extra_meat: 'لحم إضافي', extra_cheese: 'جبنة إضافية', extra_vegetable: 'خضار إضافية', extra_sauce: 'صلصة إضافية' },
  },
  vi: {
    menuSelection: 'Chọn menu', menuQuantity: 'Số lượng', required: 'Bắt buộc', optional: 'Tùy chọn',
    multipleSelection: 'Chọn nhiều', unlimited: 'Không giới hạn', exactCount: 'Chính xác {count}', rangeCount: '{min}~{max}',
    quantityControl: 'Điều khiển số lượng', addToCart: 'Thêm vào giỏ',
    pleaseSelectRequired: 'Vui lòng chọn các tùy chọn bắt buộc', pleaseSelectExactly: 'Vui lòng chọn chính xác {count} tùy chọn',
    pleaseSelectMinimum: 'Vui lòng chọn ít nhất {count} tùy chọn', pleaseSelectRange: 'Vui lòng chọn {min}~{max} tùy chọn',
    pleaseSelectOption: 'Vui lòng chọn một tùy chọn', maximumSelection: 'Bạn có thể chọn tối đa {max} tùy chọn',
    inputTypePending: 'Các tùy chọn nhập sẽ sớm được hỗ trợ',
    validationMinValue: 'Nhập ít nhất {min}', validationMaxValue: 'Nhập tối đa {max}',
    validationMinLength: 'Nhập ít nhất {min} ký tự', validationMaxLength: 'Nhập tối đa {max} ký tự',
    validationPattern: 'Định dạng không hợp lệ', validationNumberRequired: 'Nhập một số',
    menu: { complexTestMenu: 'Menu kiểm tra với tùy chọn phức tạp', complexTestMenuDescription: 'Menu kiểm tra hỗ trợ tất cả các loại điều kiện tùy chọn' },
    optionTypes: { required: 'Bắt buộc', optional: 'Tùy chọn', requiredSingleDescription: 'Chọn 1 bắt buộc', optionalSingleDescription: 'Chọn 0~1', multiUnlimitedDescription: 'Chọn không giới hạn', multiExactDescription: 'Chính xác {count}', multiRangeDescription: 'Chọn {min}~{max}', quantityControlDescription: 'Chọn theo số lượng' },
    selectionStatus: { totalCount: 'Tổng cộng {count}', selectedCount: 'Đã chọn {count}' },
    optionGroupDescriptions: { selectMainIngredient: 'Vui lòng chọn nguyên liệu chính', selectSize: 'Bạn có thể chọn kích cỡ', selectToppings: 'Chọn nhiều topping tùy thích', selectSauces: 'Vui lòng chọn nước sốt', selectSpiceLevel: 'Chọn mức độ cay', selectExtras: 'Chọn các tùy chọn thêm' },
    optionGroups: { mainChoice: 'Lựa chọn chính', sideChoice: 'Lựa chọn phụ', size: 'Kích cỡ', spiceLevel: 'Mức độ cay', extras: 'Thêm', sauce: 'Nước sốt', temperature: 'Nhiệt độ', toppings: 'Topping' },
    options: { beef: 'Thịt bò', chicken: 'Thịt gà', pork: 'Thịt heo', salmon: 'Cá hồi', friedPotato: 'Khoai tây nướng', rice: 'Cơm', salad: 'Salad', soup: 'Súp', small: 'Cỡ nhỏ', medium: 'Cỡ vừa', large: 'Cỡ lớn', cheese: 'Phô mai', bacon: 'Thịt xông khói', mushroom: 'Nấm', onion: 'Hành', lettuce: 'Xà lách', ketchup: 'Tương cà', mustard: 'Mù tạt', mayo: 'Sốt mayonnaise', bbq: 'Sốt BBQ', hot: 'Sốt cay', garlic: 'Sốt tỏi', mild: 'Nhẹ', medium_spice: 'Vừa', spicy: 'Cay', extra_spicy: 'Rất cay', super_spicy: 'Siêu cay', extra_meat: 'Thêm thịt', extra_cheese: 'Thêm phô mai', extra_vegetable: 'Thêm rau', extra_sauce: 'Thêm sốt' },
  },
  th: {
    menuSelection: 'เลือกเมนู', menuQuantity: 'จำนวน', required: 'จำเป็น', optional: 'ทางเลือก',
    multipleSelection: 'เลือกหลายตัว', unlimited: 'ไม่จำกัด', exactCount: 'ตรง {count}', rangeCount: '{min}~{max}',
    quantityControl: 'ควบคุมจำนวน', addToCart: 'เพิ่มลงตะกร้า',
    pleaseSelectRequired: 'โปรดเลือกตัวเลือกที่จำเป็น', pleaseSelectExactly: 'โปรดเลือกตรง {count} ตัวเลือก',
    pleaseSelectMinimum: 'โปรดเลือกอย่างน้อย {count} ตัวเลือก', pleaseSelectRange: 'โปรดเลือก {min}~{max} ตัวเลือก',
    pleaseSelectOption: 'โปรดเลือกหนึ่งตัวเลือก', maximumSelection: 'คุณสามารถเลือกได้สูงสุด {max} ตัวเลือก',
    inputTypePending: 'ตัวเลือกการป้อนจะรองรับเร็วๆ นี้',
    validationMinValue: 'กรอกอย่างน้อย {min}', validationMaxValue: 'กรอกสูงสุด {max}',
    validationMinLength: 'กรอกอย่างน้อย {min} ตัวอักษร', validationMaxLength: 'กรอกสูงสุด {max} ตัวอักษร',
    validationPattern: 'รูปแบบไม่ถูกต้อง', validationNumberRequired: 'กรอกตัวเลข',
    menu: { complexTestMenu: 'เมนูทดสอบตัวเลือกที่ซับซ้อน', complexTestMenuDescription: 'เมนูทดสอบที่รองรับเงื่อนไขตัวเลือกทุกประเภท' },
    optionTypes: { required: 'จำเป็น', optional: 'ทางเลือก', requiredSingleDescription: 'เลือก 1 จำเป็น', optionalSingleDescription: 'เลือก 0~1', multiUnlimitedDescription: 'เลือกไม่จำกัด', multiExactDescription: 'ตรง {count}', multiRangeDescription: 'เลือก {min}~{max}', quantityControlDescription: 'เลือกตามจำนวน' },
    selectionStatus: { totalCount: 'รวม {count}', selectedCount: 'เลือก {count}' },
    optionGroupDescriptions: { selectMainIngredient: 'โปรดเลือกส่วนผสมหลัก', selectSize: 'คุณสามารถเลือกขนาด', selectToppings: 'เลือก topping เท่าที่ต้องการ', selectSauces: 'โปรดเลือกซอส', selectSpiceLevel: 'เลือกระดับความเผ็ด', selectExtras: 'เลือกตัวเลือกเพิ่มเติม' },
    optionGroups: { mainChoice: 'ตัวเลือกหลัก', sideChoice: 'ตัวเลือกข้าง', size: 'ขนาด', spiceLevel: 'ระดับความเผ็ด', extras: 'พิเศษ', sauce: 'ซอส', temperature: 'อุณหภูมิ', toppings: 'หน้า' },
    options: { beef: 'เนื้อวัว', chicken: 'ไก่', pork: 'หมู', salmon: 'แซลมอน', friedPotato: 'มันฝรั่งอบ', rice: 'ข้าว', salad: 'สลัด', soup: 'ซุป', small: 'ขนาดเล็ก', medium: 'ขนาดกลาง', large: 'ขนาดใหญ่', cheese: 'ชีส', bacon: 'เบคอน', mushroom: 'เห็ด', onion: 'หัวหอม', lettuce: 'ผักกาด', ketchup: 'ซอสมะเขือเทศ', mustard: 'มัสตาร์ด', mayo: 'มายองเนส', bbq: 'ซอสบาร์บีคิว', hot: 'ซอสเผ็ด', garlic: 'ซอสกระเทียม', mild: 'อ่อน', medium_spice: 'กลาง', spicy: 'เผ็ด', extra_spicy: 'เผ็ดมาก', super_spicy: 'เผ็ดสุดๆ', extra_meat: 'เนื้อพิเศษ', extra_cheese: 'ชีสพิเศษ', extra_vegetable: 'ผักพิเศษ', extra_sauce: 'ซอสพิเศษ' },
  },
  nl: {
    menuSelection: 'Menukeuze', menuQuantity: 'Aantal', required: 'Verplicht', optional: 'Optioneel',
    multipleSelection: 'Meervoudige selectie', unlimited: 'Onbeperkt', exactCount: 'Precies {count}', rangeCount: '{min}~{max}',
    quantityControl: 'Aantalcontrole', addToCart: 'Toevoegen aan winkelwagen',
    pleaseSelectRequired: 'Selecteer de verplichte opties', pleaseSelectExactly: 'Selecteer precies {count} opties',
    pleaseSelectMinimum: 'Selecteer minimaal {count} opties', pleaseSelectRange: 'Selecteer {min}~{max} opties',
    pleaseSelectOption: 'Selecteer een optie', maximumSelection: 'U kunt maximaal {max} opties selecteren',
    inputTypePending: 'Invoeropties worden binnenkort ondersteund',
    validationMinValue: 'Voer minimaal {min} in', validationMaxValue: 'Voer maximaal {max} in',
    validationMinLength: 'Voer minimaal {min} tekens in', validationMaxLength: 'Voer maximaal {max} tekens in',
    validationPattern: 'Ongeldig formaat', validationNumberRequired: 'Voer een nummer in',
    menu: { complexTestMenu: 'Testmenu met complexe opties', complexTestMenuDescription: 'Een testmenu dat alle soorten optievoorwaarden ondersteunt' },
    optionTypes: { required: 'Verplicht', optional: 'Optioneel', requiredSingleDescription: 'Selecteer 1 verplicht', optionalSingleDescription: 'Selecteer 0~1', multiUnlimitedDescription: 'Onbeperkte selectie', multiExactDescription: 'Precies {count}', multiRangeDescription: 'Selecteer {min}~{max}', quantityControlDescription: 'Selectie per aantal' },
    selectionStatus: { totalCount: 'Totaal {count}', selectedCount: '{count} geselecteerd' },
    optionGroupDescriptions: { selectMainIngredient: 'Selecteer het hoofdingrediënt', selectSize: 'U kunt een maat selecteren', selectToppings: 'Selecteer zoveel toppings als u wilt', selectSauces: 'Selecteer een saus', selectSpiceLevel: 'Selecteer kruidenniveau', selectExtras: 'Selecteer extra opties' },
    optionGroups: { mainChoice: 'Hoofdkeuze', sideChoice: 'Bijgerechtkeuze', size: 'Maat', spiceLevel: 'Kruidenniveau', extras: 'Extra’s', sauce: 'Saus', temperature: 'Temperatuur', toppings: 'Toppings' },
    options: { beef: 'Rundvlees', chicken: 'Kip', pork: 'Varkensvlees', salmon: 'Zalm', friedPotato: 'Gebakken aardappel', rice: 'Rijst', salad: 'Salade', soup: 'Soep', small: 'Klein', medium: 'Middel', large: 'Groot', cheese: 'Kaas', bacon: 'Spek', mushroom: 'Champignons', onion: 'Ui', lettuce: 'Sla', ketchup: 'Ketchup', mustard: 'Mosterd', mayo: 'Mayonaise', bbq: 'BBQ-saus', hot: 'Pittige saus', garlic: 'Knoflooksaus', mild: 'Mild', medium_spice: 'Middel', spicy: 'Pittig', extra_spicy: 'Extra pittig', super_spicy: 'Super pittig', extra_meat: 'Extra vlees', extra_cheese: 'Extra kaas', extra_vegetable: 'Extra groenten', extra_sauce: 'Extra saus' },
  },
};
